import store from "@lhd/app/store";
import TextToolTip from "@lhd/components/TextToolTip";
import {
  DATE_TIME_FORMAT,
  IMAGE_FILE_EXTENSIONS,
  OFFICE_FILE_EXTENSIONS,
  REGEX,
  SERVICES,
  VIDEO_FILE_EXTENSIONS,
} from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import axios from "@lhd/libs/axios";
import axiosV2 from "@lhd/libs/axiosV2";
import { CompanyLocation, CompanyPhoneNumber, Prefecture } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import dayjs from "dayjs";
import { cloneDeep, get, isNil, lowerCase } from "lodash";
import moment from "moment";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import { AiOutlineBranches, AiOutlinePhone } from "react-icons/ai";
import { BsFillDiagram2Fill } from "react-icons/bs";
import { FaUserAlt } from "react-icons/fa";
import { RiOrganizationChart } from "react-icons/ri";
import { VscOrganization } from "react-icons/vsc";
import { v4 as uuidv4 } from "uuid";
import type { App } from "types/app.type";
import {
  APP_PART_TYPES,
  EMPLOYEE_INFO_CHILDREN_SELECTION,
  GROUP_AS_COLORS,
  UNIT_OF_MEASURE_TYPE,
} from "./AppForm/constants";
import { AppPartType } from "./AppForm/types";
import { parseJson } from "@lhd/utility";
import { getCustomerName } from "@lhd/features/Customers/CustomerListV2/utility";
export { getCustomerName };
export const validateEmail = (email: string) => {
  return String(email).toLowerCase().match(REGEX.EMAIL);
};

export const isJapaneseUp = (text: string): boolean => {
  const japaneseRegex =
    /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]/u;

  return japaneseRegex.test(String(text));
};
export const isJapanese = (text: string): boolean => {
  const japaneseRegex =
    /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]/u;
  return japaneseRegex.test(String(text || ""));
};

const addZero = (i: number | string) => {
  if (Number(i) < 10) {
    i = "0" + i;
  }
  return i;
};
export const formatDate = (value: string) => {
  const isValid = moment(value).isValid();
  return value && isValid ? moment(value).format(DATE_TIME_FORMAT.DATE_JP) : "";
};
export const formatTime = (value: string) => {
  const isValid = dayjs(value).isValid();
  return value && isValid ? dayjs(value).format(DATE_TIME_FORMAT.TIME) : "";
};
export const formatDateTime = (value: string) => {
  const isValid = moment(value).isValid();
  return value && isValid
    ? moment(value).format(DATE_TIME_FORMAT.DATE_JP_TIME)
    : "";
};
export const generateUuid = () => uuidv4();

export const getTimezoneString = () => {
  const timezoneUser = new Date().getTimezoneOffset() / -60;

  const sign = timezoneUser >= 0 ? "+" : "-";
  const hours = Math.abs(Math.floor(timezoneUser)).toString().padStart(2, "0");
  const minutes = ((Math.abs(timezoneUser) * 60) % 60)
    .toString()
    .padStart(2, "0");
  const timeZoneString = `${sign}${hours}:${minutes}`;
  return timeZoneString;
};

export const validateUrl = (userInput: string) => {
  const res = userInput?.match(
    // eslint-disable-next-line no-useless-escape
    REGEX.URL,
  );
  if (res == null) return false;
  else return true;
};

export const handleMessageError = (
  msgError: any,
  defaultMessage = "保存に失敗しました。",
) => {
  let error = defaultMessage;
  if (msgError && Array.isArray(msgError) && typeof msgError[0] === "string") {
    error = msgError[0];
  } else if (
    msgError &&
    typeof msgError === "object" &&
    Object.keys(msgError).length > 0 &&
    typeof msgError?.[Object.keys(msgError)?.[0]]?.[0] === "string"
  ) {
    error = msgError?.[Object.keys(msgError)?.[0]]?.[0];
  } else if (typeof msgError === "string") {
    error = msgError;
  }
  return error;
};

export const createFieldCode = (fieldName: string, layout: AppPartType[]) => {
  const currentFieldCodeList = layout
    .filter((ap) => ap.field_code?.includes(fieldName))
    .map((ap) => ap.field_code || "");
  if (currentFieldCodeList?.length) {
    let newIndex = 0;
    currentFieldCodeList.forEach((fieldCode) => {
      const splitBySplash = fieldCode.split("_");
      newIndex =
        splitBySplash.length > 1 &&
        !isNaN(parseInt(splitBySplash?.at(-1) || ""))
          ? Math.max(Number(splitBySplash?.at(-1)), newIndex)
          : newIndex;
    });

    // if index is exist (length > 1 and last item is number)
    // plus the value by 1
    // if not, index = 1
    newIndex =
      newIndex == 0
        ? currentFieldCodeList.length > 1
          ? newIndex + 1
          : newIndex
        : newIndex + 1;
    return `${fieldName}_${newIndex}`;
  } else {
    return fieldName;
  }
};

export const getOrgsData = async (id: string, company_id?: number | string) => {
  const rs = await axios({
    method: "get",
    url: SERVICES.ORG_NODES,
    params: {
      page: 1,
      per_page: 9999,
      offset: 0,
      "q[org_id_eq]": id,
      "q[company_id_eq]": company_id || getCompanyId(),
    },
  });
  return rs.data;
};

export const getWorkers = async (company_id?: number | string) => {
  const rs = await axiosV2({
    method: "get",
    url: SERVICES.WORKERS + "/list_less_fields",
    params: {
      page: 1,
      per_page: 10000,
      "q[company_id_eq]": company_id || getCompanyId(),
      company_id: company_id || getCompanyId(),
    },
  });
  return rs.data;
};

export const getCompanyPhoneNumbersOptions = async (
  company_id?: number | string,
) => {
  const rs = await axiosV2({
    method: "get",
    url: SERVICES.COMPANY_PHONE_NUMBERS,
    params: {
      page: 1,
      per_page: 1000,
      company_id: company_id || getCompanyId(),
    },
  });
  const dataRaw = rs.data as CompanyPhoneNumber[];
  const dataOptions =
    dataRaw
      ?.filter((item) => {
        if (item.fax?.trim() === "" || !item.fax) return false;
        return true;
      })
      .map((item) => ({
        name: item.fax || "",
        id: item.id,
      })) || [];
  return dataOptions;
};

export const getListWorkerTextField = async () => {
  const rs = await axios({
    method: "get",
    url: "/workers/list-text-fields",
    params: {
      page: 1,
      per_page: 9999,
      offset: 0,
      "q[company_id_eq]": getCompanyId(),
    },
  });
  return rs.data;
};
export const getListValuesOfField = async (fieldName: string) => {
  const rs = await axios({
    method: "get",
    url: "/workers/values-off-field",
    params: {
      page: 1,
      per_page: 9999,
      offset: 0,
      field_name: fieldName,
      "q[company_id_eq]": getCompanyId(),
    },
  });
  return rs.data;
};

export const getWorkerFieldName = (text: string) => {
  switch (text) {
    case "worker_no":
      return "";
    case "trade_name":
      return "";

    default:
      return "";
  }
};

export const workerTextFieldOptions = [
  {
    label: "姓",
    value: "first_name",
  },
  {
    label: "名",
    value: "last_name",
  },
  {
    label: "email",
    value: "email",
  },
  {
    label: "カナ",
    value: "full_name_kana",
  },
  {
    label: "スタッフ番号",
    value: "worker_no",
  },
  {
    label: "商号",
    value: "trade_name",
  },
  {
    label: "銀行名",
    value: "bank_name",
  },
  {
    label: "支店名",
    value: "bank_branch",
  },
  {
    label: "口座種別",
    value: "bank_type",
  },
  {
    label: "口座番号",
    value: "bank_number",
  },
  {
    label: "住所1",
    value: "address",
  },
  {
    label: "住所2",
    value: "address2",
  },
  {
    label: "マンション名",
    value: "apartment_name",
  },
  {
    label: "携帯電話",
    value: "tel",
  },
  {
    label: "標準報酬月額",
    value: "base_salary",
  },
  {
    label: "標準報酬月額",
    value: "base_salary",
  },
];

export const sortDataByCreatedAt = (data: any[]) => {
  const sorted = cloneDeep(data).sort((data1, data2) => {
    if (!data1.created_at || !data2.created_at) return 0;
    const date1 = new Date(data1.created_at).getTime();
    const date2 = new Date(data2.created_at).getTime();
    if (date1 > date2) return 1;
    return -1;
  });
  return sorted;
};

export const extractContent = (s: string) => {
  if (!s) return "";

  const parser = new DOMParser();

  const doc = parser.parseFromString(s, "text/html");

  return doc.body.textContent || "";
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const isNumber = (v: any) =>
  typeof v === "number" ||
  (typeof v === "string" && Number.isFinite(+v) && v.trim() !== "") ||
  !isNaN(Number(v));

export const numberInputOnWheelPreventChange = (e: any) => {
  // Prevent the input value change
  e?.target?.blur();

  // Prevent the page/container scrolling
  e?.stopPropagation();

  // Refocus immediately, on the next tick (after the current function is done)
  setTimeout(() => {
    e?.target?.focus();
  }, 0);
};

export const calculateAgeFromBirthday = (birthdateString: string) => {
  const today = new Date();
  const birthDate = new Date(birthdateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // Adjust for months and days
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

export const getExtensionByUrl = (url: string) => {
  if (!url) return "";
  const parts = url.split(".");
  return parts.length > 1 ? parts.pop() : "";
};

export const getIdFromUrlAttactment = (url: string) => url.split("/")[6];

export const fileCanPreview = (url: string) => {
  const extension = getExtensionByUrl(url);
  if (extension && OFFICE_FILE_EXTENSIONS.includes(lowerCase(extension))) {
    return extension;
  }
};

export const getNameFileByUrl = (url: string) => {
  if (!url) return "";
  const nameArray = decodeURI(url).split("/");
  const nameFile = nameArray[nameArray.length - 1];
  return nameFile;
};

export const checkIsVideoByLink = (link: string) => {
  const extension = link ? getExtensionByUrl(link) : "";

  if (extension && VIDEO_FILE_EXTENSIONS.includes(extension.toLowerCase())) {
    return true;
  } else return false;
};

export const checkIsImageByLink = (link: string) => {
  const extension = link ? getExtensionByUrl(link) : "";

  if (extension && IMAGE_FILE_EXTENSIONS.includes(lowerCase(extension))) {
    return true;
  } else return false;
};

export const handleDownload = async (
  url: string,
  controller: AbortController,
  setDownloadState?: React.Dispatch<
    React.SetStateAction<
      | {
          status: "exception" | undefined;
          percent: number;
          isLoading: boolean;
        }
      | undefined
    >
  >,
) => {
  try {
    // Set initial state: downloading started
    setDownloadState &&
      setDownloadState({
        status: undefined,
        percent: 0,
        isLoading: true,
      });

    // Fetch the image using axios
    const response = await axios.get(url, {
      responseType: "blob", // Ensure the response is treated as a Blob
      onDownloadProgress: (progressEvent) => {
        // Calculate the download progress percentage
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1), // Avoid division by zero
        );

        // Update the download progress in the state
        setDownloadState &&
          setDownloadState({
            status: undefined,
            percent: percentCompleted,
            isLoading: true,
          });
      },
      signal: controller.signal,
    });

    // Create a URL for the downloaded Blob
    const imageURL = URL.createObjectURL(response.data);

    // Extract the file name from the URL
    const nameArray = decodeURI(url).split("/");
    const nameFile = nameArray[nameArray.length - 1];

    // Create a link element to trigger the download
    const link = document.createElement("a");
    link.href = imageURL;
    link.download = nameFile;
    document.body.appendChild(link); // Append to the document body (required for Firefox)
    link.click(); // Trigger the download

    // Clean up the link and revoke the object URL
    document.body.removeChild(link);
    URL.revokeObjectURL(imageURL);

    // Set final state: download completed
    setDownloadState &&
      setDownloadState({
        status: undefined,
        percent: 100,
        isLoading: false,
      });
  } catch (error) {
    console.error("Error downloading the image:", error);

    // Set state: download failed
    setDownloadState &&
      setDownloadState({
        status: "exception",
        percent: 0,
        isLoading: false,
      });
  }
};

export const trimHTML = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const children = Array.from(doc.body.children);
  let left = 0;
  let right = children.length - 1;
  let leftHasData = false;
  let rightHasData = false;
  while ((!rightHasData || !leftHasData) && left < right) {
    if (children[left].textContent?.trim()) leftHasData = true;
    else if (!leftHasData) left++;

    if (children[right].textContent?.trim()) rightHasData = true;
    else if (!rightHasData) right--;
  }
  const newHtml = children
    .slice(left, right - left + 1)
    .map((x) => x.outerHTML)
    .join("");
  return newHtml;
};

export const getGroupType = (type?: string) => {
  if (!type || !GROUP_AS_COLORS[type as keyof typeof GROUP_AS_COLORS])
    return GROUP_AS_COLORS.typeColumns;
  return GROUP_AS_COLORS[type as keyof typeof GROUP_AS_COLORS];
};

export const getMinXAndMarginLeft = (appPartTypes: AppPartType[]) => {
  const firstItem = appPartTypes[0];
  if (!firstItem)
    return {
      minX: 0,
      maxRightX: 0,
    };
  let minX = firstItem?.item_config_data.x;
  let maxRightX = firstItem?.item_config_data.x + firstItem?.item_config_data.w;
  appPartTypes.forEach((appPartType) => {
    minX = Math.min(appPartType.item_config_data.x, minX);
    maxRightX = Math.max(
      appPartType.item_config_data.x + appPartType.item_config_data.w,
      maxRightX,
    );
  });

  return {
    maxRightX,
    minX,
  };
};

export const getBottomAndMinYMinX = (appPartTypes: AppPartType[]) => {
  const firstItem = appPartTypes[0];
  if (!firstItem)
    return {
      minY: 0,
      maxBottomY: 0,
      minX: 0,
    };
  let minY = firstItem?.item_config_data.y;
  let minX = firstItem?.item_config_data.x;

  let maxBottomY =
    firstItem?.item_config_data.y + firstItem?.item_config_data.h;
  appPartTypes.forEach((appPartType) => {
    minY = Math.min(appPartType.item_config_data.y, minY);
    minX = Math.min(appPartType.item_config_data.x, minX);

    maxBottomY = Math.max(
      appPartType.item_config_data.y + appPartType.item_config_data.h,
      maxBottomY,
    );
  });

  return {
    maxBottomY,
    minY,
    minX,
  };
};

export function groupItemsByRow(items: AppPartType[]): any[][] {
  const data: { minY: number; bottomY: number; items: AppPartType[] }[] = [];
  items
    .sort(
      (x: AppPartType, y: AppPartType) =>
        x.item_config_data.y - y.item_config_data.y,
    )
    .forEach((item, index) => {
      const y = item.item_config_data.y;
      const bottomY = item.item_config_data.h + y;

      if (data.length === 0) {
        data.push({
          minY: y,
          bottomY,
          items: [item],
        });
      } else {
        const itemData = data.find((a) => a.minY <= y && a.bottomY > y);
        if (itemData) {
          itemData.items.push(item);
          itemData.bottomY = Math.max(bottomY, itemData.bottomY);
          itemData.minY = Math.min(y, itemData.minY);
        } else {
          data.push({
            minY: y,
            bottomY,
            items: [item],
          });
        }
      }
    });
  return data.map((x) => x.items);
}

export const getLayoutWithMarginTop = (
  newLayoutByRow: ReturnType<typeof groupItemsByRow>,
) => {
  let preMaxBottomY = 0;
  return newLayoutByRow.map((items, index) => {
    const { maxBottomY, minY, minX } = getBottomAndMinYMinX(items);
    const marginTop = minY - preMaxBottomY;
    preMaxBottomY = maxBottomY;
    return {
      items,
      minY,
      minX,
      maxBottomY,
      marginTop,
    };
  });
};

export function groupItemsCol(items: AppPartType[]) {
  const columns: any[] = [];
  const itemsSorted = items.sort(
    (a, b) => a.item_config_data.x - b.item_config_data.x,
  );
  for (const item of itemsSorted) {
    const matchedColumn = columns.find(
      (col) =>
        item.item_config_data.x >= col.rangeStart &&
        item.item_config_data.x <= col.rangeEnd - 2,
    );

    if (matchedColumn) {
      matchedColumn.items.push(item);
      matchedColumn.rangeStart = Math.min(
        matchedColumn.rangeStart,
        item.item_config_data.x,
      );
      matchedColumn.rangeEnd = Math.max(
        matchedColumn.rangeEnd,
        item.item_config_data.x + item.item_config_data.w,
      );
    } else {
      columns.push({
        rangeStart: item.item_config_data.x,
        rangeEnd: item.item_config_data.x + item.item_config_data.w,
        items: [item],
      });
    }
  }

  return columns.map((col) => {
    return col.items;
  });
}

export const getConfigAppPartType = (item: AppPartType) => {
  const isHiddenLabel = item.is_hide_name;
  const minChar = item.min_num_char || item.item_config_data?.min_num_char;
  const maxChar = item.max_num_char || item.item_config_data?.max_num_char;
  const nameItem = item.id?.toString() as string;
  const nameItemParent = item.parent_id?.toString() as string;
  const namePath = nameItemParent
    ? [nameItemParent + "", nameItem + ""]
    : [nameItem + ""];
  const isAllowEditColumn =
    item?.current_detail_permissions?.specified_app_part_edit;

  const isAllowViewField =
    item?.current_detail_permissions?.specified_app_part_read ||
    item?.current_detail_permissions?.specified_app_part_edit;

  const labelText = item.label_text || "";
  const appPartType = item.app_part_type;
  return {
    namePath,
    isHiddenLabel,
    minChar,
    maxChar,
    nameItem,
    isAllowEditColumn,
    isAllowViewField,
    labelText,
    appPartType,
  };
};

let primaryCompanyNameCache: {
  companyId: string;
  companies: { id: number; name: string }[] | undefined;
  name: string;
} | null = null;

export const getPrimaryCompanyNameDisplayText = () => {
  const companyId = String(getCompanyId() || "");
  const companies = user(store.getState())?.companies;

  if (
    primaryCompanyNameCache &&
    primaryCompanyNameCache.companyId === companyId &&
    primaryCompanyNameCache.companies === companies
  ) {
    return primaryCompanyNameCache.name;
  }

  const name =
    companies?.find((item) => item.id.toString() == companyId)?.name || "";
  primaryCompanyNameCache = { companyId, companies, name };
  return name;
};

export const getPositionsOptions = async () => {
  try {
    const companyId = getCompanyId();
    const res = await axios({
      method: "get",
      url: SERVICES.POSITIONS,
      params: {
        page: 1,
        per_page: 1000,
        "q[company_id_eq]": companyId,
      },
    });
    return (res.data || []).map((pos: any) => ({
      id: pos.id,
      name: pos.name,
    }));
  } catch (e) {
    return [];
  }
};

export const getClientsOptions = async (companyId = getCompanyId()) => {
  try {
    const res = await axiosV2({
      method: "get",
      url: SERVICES.CLIENTS,
      params: {
        page: 1,
        per_page: 1000,
        "q[company_id_eq]": companyId,
        current_login_company_id: companyId,
        target_tab: "registration_information",
      },
    });
    return (res.data || []).map((client: any) => ({
      ...client,
      id: client.id,
      name: getCustomerName(client) || client?.client_name?.name || "",
    }));
  } catch (e) {
    return [];
  }
};

export const getSelectionOptionsKey = (appPartType?: string) => {
  if (appPartType === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION) {
    return APP_PART_TYPES.WORKER_SELECTION;
  }
  return appPartType;
};

export const isWorkerNameSelection = (appPartType?: string) =>
  appPartType === APP_PART_TYPES.WORKER_SELECTION ||
  appPartType === APP_PART_TYPES.REPRESENTATIVE_SELECTION ||
  appPartType === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION;

export const isSingleAppPartSelection = (appPartType?: string) =>
  appPartType === APP_PART_TYPES.COMPANY_LOCATION_SELECTION ||
  appPartType === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION;

export const isAutoFillSelectionType = (type?: string) => false;
// type === APP_PART_TYPES.COMPANY_LOCATION_SELECTION ||
// type === APP_PART_TYPES.REPRESENTATIVE_SELECTION ||
// type === APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION ||
// type === APP_PART_TYPES.POSITION_SELECTION ||
// type === APP_PART_TYPES.CUSTOMER_SELECTION;

export const isSelectionArrayAppPartType = (type?: string) =>
  type === APP_PART_TYPES.ORGANIZATION_SELECTION ||
  type === APP_PART_TYPES.DIVISION_SELECTION ||
  type === APP_PART_TYPES.DEPARTMENT_SELECTION ||
  type === APP_PART_TYPES.BRANCH_SELECTION ||
  type === APP_PART_TYPES.WORKER_SELECTION ||
  type === APP_PART_TYPES.REPRESENTATIVE_SELECTION ||
  type === APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION ||
  type === APP_PART_TYPES.POSITION_SELECTION ||
  type === APP_PART_TYPES.CUSTOMER_SELECTION;

export const formatSelectionValueSubmit = (value: any): string => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === '""' ||
    value === "null" ||
    value === "[]"
  ) {
    return "[]";
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(
      (item) =>
        item !== undefined &&
        item !== null &&
        item !== "" &&
        item !== '""' &&
        item !== "null",
    );
    return filtered.length ? JSON.stringify(filtered.map(String)) : "[]";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      trimmed === "" ||
      trimmed === '""' ||
      trimmed === "null" ||
      trimmed === "[]"
    ) {
      return "[]";
    }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(
            (item: any) =>
              item !== undefined &&
              item !== null &&
              item !== "" &&
              item !== '""' &&
              item !== "null",
          );
          return filtered.length ? JSON.stringify(filtered.map(String)) : "[]";
        }
      } catch {
        return "[]";
      }
    }
    return JSON.stringify([trimmed]);
  }
  return JSON.stringify([String(value)]);
};

export const stringifyEmployeeInfoValue = (value: any) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === '""' ||
    value === "null" ||
    value === "[]"
  ) {
    return "[]";
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(
      (item) =>
        item !== undefined &&
        item !== null &&
        item !== "" &&
        item !== '""' &&
        item !== "null",
    );
    return filtered.length ? JSON.stringify(filtered.map(String)) : "[]";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      trimmed === "" ||
      trimmed === '""' ||
      trimmed === "null" ||
      trimmed === "[]"
    ) {
      return "[]";
    }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(
            (item: any) =>
              item !== undefined &&
              item !== null &&
              item !== "" &&
              item !== '""' &&
              item !== "null",
          );
          return filtered.length ? JSON.stringify(filtered.map(String)) : "[]";
        }
      } catch {
        return "[]";
      }
    }
    return JSON.stringify([trimmed]);
  }
  return JSON.stringify([String(value)]);
};

export const parseEmployeeInfoFormValue = (value: any) => {
  if (value === undefined || value === null || value === "") return undefined;
  try {
    const parsed =
      typeof value === "string" && value.trim().startsWith("[")
        ? JSON.parse(value)
        : value;
    if (Array.isArray(parsed)) {
      return parsed[0] !== undefined && parsed[0] !== null
        ? String(parsed[0])
        : undefined;
    }
    return String(parsed);
  } catch {
    return String(value);
  }
};

export const EMPLOYEE_INFO_CHILD_APP_PART_TYPES =
  EMPLOYEE_INFO_CHILDREN_SELECTION.map((item) => item.app_part_type);

export const isEmployeeInfoChildAppPartType = (type?: string) =>
  !!type &&
  EMPLOYEE_INFO_CHILD_APP_PART_TYPES.includes(
    type as AppPartType["app_part_type"],
  );

export const flattenAppPartsTree = (
  appParts?: AppPartType[],
): AppPartType[] => {
  if (!appParts?.length) return [];
  return appParts.flatMap((part) => [
    part,
    ...flattenAppPartsTree(part.children),
  ]);
};

export const findEmployeeInfoParentPart = (
  child?: AppPartType,
  appParts?: AppPartType[],
) => {
  const parentI = parseJson(
    child?.item_config_data,
    child?.item_config_data,
  )?.employee_info_parent_i;
  if (!parentI) return undefined;
  return flattenAppPartsTree(appParts).find(
    (part) =>
      part.app_part_type === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION &&
      parseJson(part.item_config_data, part.item_config_data)?.i === parentI,
  );
};

export const getEmployeeInfoChildrenOfParent = (
  parent?: AppPartType,
  appParts?: AppPartType[],
) => {
  const parentI = parent?.item_config_data?.i;
  if (!parentI) return [];
  return flattenAppPartsTree(appParts).filter(
    (part) => part.item_config_data?.employee_info_parent_i === parentI,
  );
};

export const buildEmployeeInfoChildValue = (
  parentWorkerId?: string | number | null,
  appPartType?: string,
) => {
  if (
    parentWorkerId === undefined ||
    parentWorkerId === null ||
    parentWorkerId === "" ||
    !appPartType
  ) {
    return "";
  }
  return `${parentWorkerId}${appPartType}`;
};

export const getEmployeeInfoChildValueFromParentRaw = (
  parentRaw: any,
  appPartType?: string,
) =>
  buildEmployeeInfoChildValue(
    parseEmployeeInfoFormValue(parentRaw),
    appPartType,
  );

export const getEmployeeInfoParentFormKey = (
  childFormKey?: string,
  childPart?: AppPartType,
  parentPart?: AppPartType,
) => {
  if (!parentPart?.id) return undefined;
  const parentId = parentPart.id.toString();
  if (!childFormKey || childFormKey === childPart?.id?.toString()) {
    return parentId;
  }
  const parts = childFormKey.split("-");
  if (parts.length >= 3) {
    return `${parts[0]}-${parts[1]}-${parentId}`;
  }
  return parentId;
};

export const getEmployeeInfoChildSubmitValue = (
  childPart: AppPartType | undefined,
  childFormKey: string,
  values: Record<string, any>,
  appParts?: AppPartType[],
) => {
  const parent = findEmployeeInfoParentPart(childPart, appParts);
  const parentKey = getEmployeeInfoParentFormKey(
    childFormKey,
    childPart,
    parent,
  );
  return getEmployeeInfoChildValueFromParentRaw(
    parentKey ? values[parentKey] : undefined,
    childPart?.app_part_type,
  );
};

export const getPrefixIcon = (appPartType?: string) => {
  switch (appPartType) {
    case APP_PART_TYPES.ORGANIZATION_SELECTION:
      return <RiOrganizationChart size={18} />;
    case APP_PART_TYPES.DIVISION_SELECTION:
      return <BsFillDiagram2Fill size={18} />;
    case APP_PART_TYPES.DEPARTMENT_SELECTION:
      return <VscOrganization size={18} />;
    case APP_PART_TYPES.BRANCH_SELECTION:
      return <AiOutlineBranches size={18} />;
    case APP_PART_TYPES.WORKER_SELECTION:
    case APP_PART_TYPES.EMPLOYEE_INFO_SELECTION:
      return <AiOutlineBranches size={18} />;
    case APP_PART_TYPES.COMPANY_LOCATION_SELECTION:
      return <AiOutlineBranches size={18} />;
    case APP_PART_TYPES.REPRESENTATIVE_SELECTION:
      return <FaUserAlt size={18} />;
    case APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION:
      return <AiOutlinePhone size={18} />;
    default:
      return <div></div>;
  }
};

export const DELETED_SELECTION_TEXT = "（削除済み）　※選び直してください";
export const DELETED_SELECTION_VIEW_TEXT = "（削除済み）";

export const renderSelection = (
  props: CustomTagProps,
  appPartType?: string,
) => {
  const isDeleted =
    props.label === DELETED_SELECTION_TEXT ||
    props.label === DELETED_SELECTION_VIEW_TEXT;
  switch (appPartType) {
    case APP_PART_TYPES.ORGANIZATION_SELECTION:
    case APP_PART_TYPES.DIVISION_SELECTION:
    case APP_PART_TYPES.DEPARTMENT_SELECTION:
    case APP_PART_TYPES.BRANCH_SELECTION:
    case APP_PART_TYPES.WORKER_SELECTION:
    case APP_PART_TYPES.EMPLOYEE_INFO_SELECTION:
    case APP_PART_TYPES.COMPANY_LOCATION_SELECTION:
    case APP_PART_TYPES.REPRESENTATIVE_SELECTION:
    case APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION:
    case APP_PART_TYPES.POSITION_SELECTION:
    case APP_PART_TYPES.CUSTOMER_SELECTION:
      return (
        <div
          className={`org-infor ${isDeleted ? "deleted-selection" : ""}`}
          style={isDeleted ? { color: "#bfbfbf" } : undefined}
        >
          <TextToolTip>
            {getPrefixIcon(appPartType)}
            {props.label}
          </TextToolTip>
        </div>
      );
    default:
      return <div></div>;
  }
};

export const validatePhone = (phone: string) => {
  return String(phone)
    .toLowerCase()
    .match(/^[0-9]*$/);
};
export const isEqualTime = (value1: string, value2: string, type?: string) => {
  // if date1 or date2 is not valid date, return false;
  if (!moment(value1).isValid() || !moment(value2).isValid()) {
    return false;
  }
  const formatFn = type === "date" ? formatDate : formatDateTime;
  if (formatFn(value1) === formatFn(value2)) {
    return true;
  }
  return false;
};

export const getNameCompanyLocation = (
  record: CompanyLocation,
  prefetureList?: Prefecture[],
) => {
  const pref = (() => {
    if (typeof record?.prefecture === "object") {
      return record?.prefecture?.name;
    }
    if (typeof record?.prefecture_id === "number") {
      return prefetureList?.find((item) => item.id === record?.prefecture_id)
        ?.name;
    }
    return record?.prefecture_name || record?.prefecture || "";
  })();

  const fullAddress = [pref, record?.address, record?.apartment]
    .filter(Boolean)
    .join(" ");
  return fullAddress || record?.location_name;
};

export function formatStringAppartNumber(
  inputString?: string,
  appPartType?: AppPartType,
) {
  if (
    inputString === "" ||
    inputString === undefined ||
    inputString === null ||
    !appPartType
  ) {
    return "";
  }
  const unit_of_measure_type = appPartType.unit_of_measure_type;
  const unit_of_measure = appPartType.unit_of_measure;
  const decimal = appPartType.number_of_decimal_place;
  const isUseThousandsSeparators = appPartType.is_use_thousands_separators;
  const temp = decimal ? 10 ** decimal : 1;
  let formattedNumber = (Number(parseFloat(inputString) * temp) / temp).toFixed(
    decimal || 0,
  );
  const prefix =
    unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix ||
    unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix_suffix
      ? !isNil(unit_of_measure)
        ? unit_of_measure
        : ""
      : "";
  const suffix =
    unit_of_measure_type === UNIT_OF_MEASURE_TYPE.suffix ||
    unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix_suffix ||
    !unit_of_measure_type
      ? unit_of_measure || ""
      : "";

  const numericString = formattedNumber
    ? formattedNumber.replaceAll(" ", "").replace(/[^\d.-]/g, "")
    : "";

  const [intergerPart, decimalPlaces] = numericString.split(".");
  const formattedIntegerPart = isUseThousandsSeparators
    ? parseFloat(intergerPart).toLocaleString("en-US")
    : intergerPart;
  formattedNumber = decimalPlaces
    ? formattedIntegerPart + "." + decimalPlaces
    : formattedIntegerPart;

  // Return the formatted string with the original prefix and suffix
  return `${prefix}${formattedNumber}${suffix}`;
}

export const getWorkerDigitalSignature = (worker: any): string | null => {
  if (!worker) return null;
  if (
    typeof worker.digital_signature === "string" &&
    worker.digital_signature.length > 0
  ) {
    return worker.digital_signature;
  }
  if (worker.digital_signature?.file_url) {
    return worker.digital_signature.file_url;
  }
  if (worker.digital_signature?.url) {
    return worker.digital_signature.url;
  }
  if (worker.digital_signature_attachment?.url) {
    return worker.digital_signature_attachment.url;
  }
  if (worker.digital_signature_attachment?.file_url) {
    return worker.digital_signature_attachment.file_url;
  }
  if (worker.user?.signature_original_url) {
    return worker.user.signature_original_url;
  }
  if (worker.user?.signature?.url) {
    return worker.user.signature.url;
  }
  if (worker.signature_original_url) {
    return worker.signature_original_url;
  }
  if (
    typeof worker.electronicSignature === "string" &&
    worker.electronicSignature.length > 0
  ) {
    return worker.electronicSignature;
  }
  if (worker.electronicSignature?.file_url) {
    return worker.electronicSignature.file_url;
  }
  if (worker.electronicSignature?.url) {
    return worker.electronicSignature.url;
  }
  return null;
};

export const fetchWorkerDigitalSignature = async (
  workerId: number | string,
): Promise<string | null> => {
  if (!workerId) return null;
  try {
    const companyId = getCompanyId();
    const res = await axiosV2.get(`${SERVICES.WORKERS}/${workerId}`, {
      params: {
        current_login_company_id: companyId,
      },
    });
    return getWorkerDigitalSignature(res.data);
  } catch (e) {
    return null;
  }
};
export const getParamUrlFetchEmployeeInfoChildrenSelection = ({
  appPartType,
  workerId,
}: {
  appPartType: string;
  workerId: string | number;
}) => {
  const mapParams = {
    [APP_PART_TYPES.WORKER_ADDRESSES_SELECTION]: {
      url: `/workers/${workerId}/worker_addresses`,
      params: {},
    },
    [APP_PART_TYPES.WORKER_PHONE_NUMBERS_LANDLINE_SELECTION]: {
      url: `/workers/${workerId}/worker_phone_numbers`,
      params: {
        phone_type: "landline",
      },
    },
    [APP_PART_TYPES.WORKER_PHONE_NUMBERS_MOBILE_SELECTION]: {
      url: `/workers/${workerId}/worker_phone_numbers`,
      params: {
        phone_type: "mobile",
      },
    },
    [APP_PART_TYPES.USER_WORKER_EMAILS_SELECTION]: {
      url: `/workers/${workerId}/user_worker_emails`,
      params: {},
    },
    [APP_PART_TYPES.WORKER_STORES_SELECTION]: {
      url: `/workers/${workerId}/worker_stores`,
      params: {},
    },
    [APP_PART_TYPES.WORKER_REGISTRATION_NUMBERS_SELECTION]: {
      url: `/worker_registration_numbers`,
      params: { worker_id: workerId },
    },
    [APP_PART_TYPES.WORKER_EMERGENCY_CONTACTS_SELECTION]: {
      url: `/workers/${workerId}/worker_emergency_contacts`,
      params: {},
    },
    [APP_PART_TYPES.WORKER_EMERGENCY_ADDRESSES_SELECTION]: {
      url: `/workers/${workerId}/worker_emergency_addresses`,
      params: {},
    },
    [APP_PART_TYPES.WORKER_ONBOARDINGS_SELECTION]: {
      url: `/worker_onboardings`,
      params: { worker_id: workerId },
    },
    [APP_PART_TYPES.WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION]: {
      url: `/worker_insurances`,
      params: {
        insurance_type: "regular_insurance",
        worker_id: workerId,
      },
    },
    [APP_PART_TYPES.WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION]: {
      url: `/worker_insurances`,
      params: {
        insurance_type: "unemployment_insurance",
        worker_id: workerId,
      },
    },
    [APP_PART_TYPES.WORKER_COMPANY_EMAILS_SELECTION]: {
      url: `/worker_company_emails`,
      params: { worker_id: workerId },
    },
    [APP_PART_TYPES.WORKER_DEVICES_SELECTION]: {
      url: `/worker_devices`,
      params: { worker_id: workerId },
    },
  };
  const params = mapParams[appPartType as keyof typeof mapParams];

  if (params) {
    const commonParams = {
      current_login_company_id: getCompanyId(),
      page: 1,
      per_page: 1000,
    };
    return {
      ...params,
      params: {
        ...commonParams,
        ...params.params,
      },
    };
  }
};

export const getEmployeeInfoChildrenOptions = (
  response: any,
  appPartType?: string,
) => {
  const dataRaw = response || [];
  const mappedKeysObj = {
    [APP_PART_TYPES.WORKER_ADDRESSES_SELECTION]: [
      "prefecture.name",
      "address",
      "apartment",
    ],
    [APP_PART_TYPES.WORKER_PHONE_NUMBERS_LANDLINE_SELECTION]: ["phone_number"],
    [APP_PART_TYPES.WORKER_PHONE_NUMBERS_MOBILE_SELECTION]: ["phone_number"],
    [APP_PART_TYPES.USER_WORKER_EMAILS_SELECTION]: ["email"],
    [APP_PART_TYPES.WORKER_STORES_SELECTION]: ["store_name"],
    [APP_PART_TYPES.WORKER_REGISTRATION_NUMBERS_SELECTION]: [
      "registration_number",
    ],
    [APP_PART_TYPES.WORKER_EMERGENCY_CONTACTS_SELECTION]: [
      "first_name",
      "last_name",
    ],
    [APP_PART_TYPES.WORKER_EMERGENCY_ADDRESSES_SELECTION]: [
      "prefecture.name",
      "address",
      "apartment",
    ],
    [APP_PART_TYPES.WORKER_ONBOARDINGS_SELECTION]: ["registration_number"],
    [APP_PART_TYPES.WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION]: [
      "insurance_number",
    ],
    [APP_PART_TYPES.WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION]: [
      "insurance_number",
    ],
    [APP_PART_TYPES.WORKER_COMPANY_EMAILS_SELECTION]: ["email"],
    [APP_PART_TYPES.WORKER_DEVICES_SELECTION]: ["device_number"],
  };

  const mappedKeys = mappedKeysObj[appPartType as keyof typeof mappedKeysObj];
  if (!mappedKeys || !Array.isArray(dataRaw)) return [];

  return (
    dataRaw
      .map((item: any) => ({
        value: String(get(item, "id", "")),
        label: mappedKeys
          .map((key) => get(item, key))
          .filter((val) => val !== undefined && val !== null && val !== "")
          .join(""),
      }))
      ?.filter((item: any) => item.value && item.label) || []
  );
};

export const EMPLOYEE_INFO_CHILD_CURRENT_OPTIONS_SUFFIX = "__current_options";

export const getEmployeeInfoChildCurrentOptionsKey = (nameItem: string) =>
  `${nameItem}${EMPLOYEE_INFO_CHILD_CURRENT_OPTIONS_SUFFIX}`;

export const isEmployeeInfoChildCurrentOptionsKey = (key?: string) =>
  !!key && key.endsWith(EMPLOYEE_INFO_CHILD_CURRENT_OPTIONS_SUFFIX);

export const parseEmployeeInfoChildFormValue = (value: any): string[] => {
  if (value === undefined || value === null || value === "") return [];
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined && item !== null && item !== "")
      .map((item) => String(item));
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .filter((item: any) => !!item)
            .map((item) => String(item));
        }
      } catch {
        // fall through: treat as single id
      }
    }
    return [trimmed];
  }
  return [String(value)];
};

export const stringifyEmployeeInfoChildFormValue = (value: any): string => {
  const ids = parseEmployeeInfoChildFormValue(value);
  return JSON.stringify(ids);
};

export const parseEmployeeInfoChildCurrentOptions = (raw: any) => {
  if (!raw) return [] as any[];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [] as any[];
    }
  }
  return [] as any[];
};

type EmployeeInfoChildOption = {
  value?: string | number;
  label?: string;
  worker_id?: string | number;
};

export const stringifyEmployeeInfoChildCurrentOptions = (
  option?: EmployeeInfoChildOption | EmployeeInfoChildOption[] | null,
) => {
  const list = Array.isArray(option) ? option : option ? [option] : [];
  const normalized = list
    .filter(
      (item) =>
        item &&
        item.value !== undefined &&
        item.value !== null &&
        item.value !== "",
    )
    .map((item) => ({
      value: String(item.value),
      label: item.label ?? "",
      ...(item.worker_id !== undefined &&
      item.worker_id !== null &&
      item.worker_id !== ""
        ? { worker_id: String(item.worker_id) }
        : {}),
    }));
  return JSON.stringify(normalized);
};

export const getWorkerIdFromEmployeeInfoChildCurrentOptions = (
  currentOptions?: any,
) => {
  const options = parseEmployeeInfoChildCurrentOptions(currentOptions);
  const workerId = options?.[0]?.worker_id;
  return workerId !== undefined && workerId !== null && workerId !== ""
    ? String(workerId)
    : undefined;
};

/**
 * Normalize current_options to always be a JSON string (e.g. "[]" or '[{"value":"1","label":"x"}]').
 */
export const ensureCurrentOptionsJson = (raw: any): string => {
  if (raw === undefined || raw === null || raw === "") return "[]";
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? raw : "[]";
    } catch {
      return "[]";
    }
  }
  if (Array.isArray(raw)) {
    try {
      return JSON.stringify(raw);
    } catch {
      return "[]";
    }
  }
  if (typeof raw === "object") {
    try {
      return JSON.stringify([raw]);
    } catch {
      return "[]";
    }
  }
  return "[]";
};

export const getEmployeeInfoChildDisplayLabel = (
  value?: any,
  currentOptions?: any,
) => {
  const ids = parseEmployeeInfoChildFormValue(value);
  if (!ids.length) return "";
  const options = parseEmployeeInfoChildCurrentOptions(currentOptions);
  return ids
    .map((id) => {
      const matched = options.find(
        (option: any) => String(option?.value) === String(id),
      );
      return matched?.label
        ? String(matched.label)
        : DELETED_SELECTION_VIEW_TEXT;
    })
    .filter(Boolean)
    .join(",");
};

export const addDeletedOtionps = (values: any[], options: any[]) => {
  const deletedOptions = values
    ?.map((value) => {
      const option = options.find((x) => x.value === value);
      if (!option) {
        return { value, label: DELETED_SELECTION_TEXT };
      }
    })
    .filter((x) => x);
  return [...options, ...deletedOptions];
};

export const getEmployeeInfoChildOptionLabel = (
  appPartType?: string,
  option?: any,
) => {
  if (!option) return "";
  switch (appPartType) {
    case APP_PART_TYPES.WORKER_ADDRESSES_SELECTION:
    case APP_PART_TYPES.WORKER_EMERGENCY_ADDRESSES_SELECTION:
      return [
        option.prefecture?.name,
        option.address,
        option.address2,
        option.apartment,
      ]
        .filter(Boolean)
        .join(" ");
    case APP_PART_TYPES.WORKER_PHONE_NUMBERS_LANDLINE_SELECTION:
    case APP_PART_TYPES.WORKER_PHONE_NUMBERS_MOBILE_SELECTION:
      return option.phone_number || option.name || "";
    case APP_PART_TYPES.USER_WORKER_EMAILS_SELECTION:
    case APP_PART_TYPES.WORKER_COMPANY_EMAILS_SELECTION:
      return option.email || option.name || "";
    case APP_PART_TYPES.WORKER_STORES_SELECTION:
      return option.store_name || option.name || "";
    case APP_PART_TYPES.WORKER_REGISTRATION_NUMBERS_SELECTION:
      return option.registration_number || option.name || "";
    case APP_PART_TYPES.WORKER_EMERGENCY_CONTACTS_SELECTION:
      return (
        [option.last_name, option.first_name].filter(Boolean).join(" ") ||
        option.name ||
        ""
      );
    case APP_PART_TYPES.WORKER_ONBOARDINGS_SELECTION:
      return (
        option.worker_no || option.registration_number || option.name || ""
      );
    case APP_PART_TYPES.WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION:
    case APP_PART_TYPES.WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION:
      return option.insurance_number || option.name || "";
    case APP_PART_TYPES.WORKER_DEVICES_SELECTION:
      return option.device_number || option.phone_number || option.name || "";
    default:
      return option.name || option.label || "";
  }
};

export const isViewOnlyAppPermission = (
  permissions?: App["current_detail_permissions"],
) =>
  !!permissions?.specified_app_read &&
  !permissions.specified_app_edit &&
  !!permissions.records_permissions?.general_record_read &&
  !permissions.records_permissions.add_new_record &&
  !permissions.records_permissions.general_record_edit;
